/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { observable, action } from "mobx";
import { flattenNode } from "utils/ReadAloudUtils";
import { getIsFocusable } from "utils/AccessibilityUtils";
import { getPageOverlayApis } from "selectors/overlayDropinSelectors";
import { isRotated } from "selectors/documentSelectors";
import { getOpenAction } from "selectors/pagedViewState";
import { overlayEvents } from "overlays/OverlayEvents";
import { getCurrentPageIndex } from "selectors/pageNumber";

class AccessibilityStore {
  currActiveElemNum = -1;
  @observable shouldFetchAccContent;
  @observable isAccEnabledByDefault;
  @observable isActiveElementInPagesView;
  @observable tabbablePDFElementsInPage;
  @observable documentStatus;
  @observable isActiveElementInContextMenu;
  @observable elementToFocus;
  isCaretSetByFocus = false;
  beforeA11yTreeRenderCallback;

  constructor() {
    this.init();
  }

  initStore(docApi, stores) {
    if (docApi) {
      this.docApi = docApi;
    }
    if (stores) {
      this.stores = stores;
    }
  }

  @action
  init() {
    this.shouldFetchAccContent = false;
    this.isAccEnabledByDefault = false;
    this.elementToFocus = null;
    this.pageIndexToFocus = -1;
    this.uiElementToFocus = null;
    this.tabbablePDFElementsInPage = [];
    this.isActiveElementInPagesView = false;
    this.documentStatus = {
      label: "document.status.loading"
    };
    this.isActiveElementInContextMenu = false;
    this.beforeA11yTreeRenderCallback = null;
  }

  @action
  setShouldFetchAccContent() {
    if (!this.shouldFetchAccContent) {
      this.shouldFetchAccContent = true;
    }
  }

  setCurrActiveElemNum(value) {
    this.currActiveElemNum = value;
  }

  @action
  setAccEnabledByDefault() {
    if (!this.isAccEnabledByDefault) {
      this.isAccEnabledByDefault = true;
    }
  }

  @action.bound
  setIsActiveElementInPagesView(value) {
    this.isActiveElementInPagesView = value;
  }

  @action.bound
  setElementToFocus(element) {
    this.elementToFocus = element;
  }

  setPageIndexToFocus(pageIndex) {
    this.pageIndexToFocus = pageIndex;
  }

  setUIElementToFocus(element) {
    this.uiElementToFocus = element;
  }

  @action.bound
  setTabbablePDFElements(pageIndex, pageAccData) {
    if (pageAccData) {
      const flattenedPageAccData = [];
      if (pageAccData) pageAccData.forEach(item => flattenedPageAccData.push(...flattenNode(item.node)));
      const tabbablePDFElements = flattenedPageAccData.filter(getIsFocusable);
      if (tabbablePDFElements.length) {
        const tabbableElementsInPage = this.tabbablePDFElementsInPage.slice();
        tabbableElementsInPage[pageIndex] = tabbablePDFElements;
        this.tabbablePDFElementsInPage = tabbableElementsInPage;
      }
    }
  }

  selectComment = commentId => {
    const overlayApis = getPageOverlayApis(this.stores, "COMMENT");
    if (typeof overlayApis.commentClickHandler === "function") overlayApis.commentClickHandler(commentId);
  };

  @action
  handleCommentUpdated({ pageNum, data }) {
    const pageIndex = pageNum - 1;
    const pageAccContent = this.stores.documentStore.pages[pageIndex].pageAccText;
    if (pageAccContent) {
      let isDataUpdated = false;
      const updatedPageAccContent = pageAccContent.map(accNode => {
        if (accNode.node.contentRole === "annotation") {
          if (accNode.node.info.annotId === data.annot.id) {
            if (accNode.node.info.annotContent !== data.annot.contents) {
              accNode.node.info.annotContent = data.annot.contents;
            }
            isDataUpdated = true;
          }
        }
        return accNode;
      });
      if (isDataUpdated) {
        this.stores.documentStore.updatePageAccText(updatedPageAccContent, pageIndex);
      }
    }
  }

  scrollToFocusElementWithTab(pageIndex, element) {
    this.setElementToFocus({ pageIndex, id: element.id });
    const { xMin, yMin, xMax, yMax } = element.contentRect;
    const yToScrollTo = isRotated(this.stores) ? Math.min(xMin, xMax) : Math.min(yMin, yMax);
    this.stores.pagedViewState.goToLocation(pageIndex, 0, yToScrollTo);
  }

  setDocumentLoadComplete() {
    overlayEvents.on("pagesViewScrolled", evt => {
      const currentPageNumber = getCurrentPageIndex(this.stores) + 1;
      if (currentPageNumber !== this.documentStatus.pageNum) {
        this.setDocumentStatus("modernviewer.ariaLabel.thumbnails.pages", {
          pageNum: currentPageNumber,
          ariaLive: "polite"
        });
      }
    });
    const initialPageIndex = getOpenAction(this.stores).pageIndex || 0;
    this.setDocumentStatus("document.status.loaded", { pageNum: initialPageIndex + 1 });
  }

  @action.bound
  setDocumentStatus(label, properties) {
    this.documentStatus = {
      label,
      ...properties
    };
  }

  @action
  setIsActiveElementInContextMenu(val) {
    this.isActiveElementInContextMenu = val;
  }

  setIsCaretSetByFocus(value) {
    this.isCaretSetByFocus = value;
  }

  setBeforeA11yTreeRenderCallback(callback) {
    this.beforeA11yTreeRenderCallback = callback;
  }
}

export { AccessibilityStore };
